import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Paginator from '../../components/ui/Paginator';
import { downloadCsv } from '../../utils/csv';
import {
    downloadTemplate, IMPORT_LABELS, importProducts, ImportResult, ImportTemplate, ImportType, loadImportTemplate,
    MAX_FILE_MB, readCsvFile, RowAction, validateCsvFile,
} from './productImport';


const PAGE_SIZE = 50;

const ACTION_BADGES: Record<RowAction, { label: string; className: string }> = {
    create: { label: 'Nuevo', className: 'bg-success-subtle text-success-emphasis' },
    update: { label: 'Actualizar', className: 'bg-primary-subtle text-primary-emphasis' },
    unchanged: { label: 'Sin cambios', className: 'bg-light text-muted border' },
    error: { label: 'Con errores', className: 'bg-danger-subtle text-danger-emphasis' },
};

type Filter = 'all' | RowAction;

const formatSize = (bytes: number) => bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// Pantalla para importar productos (con o sin inventario) desde un CSV: plantilla y guía, validación con vista
// previa paginada e importación. Al terminar vuelve a la pantalla de productos con el resultado.
export const ProductImportPage = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const initialType: ImportType = new URLSearchParams(location.search).get('type') === 'inventory' ? 'inventory' : 'products';

    const [type, setType] = useState<ImportType>(initialType);
    const [template, setTemplate] = useState<ImportTemplate | null>(null);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const [showGuide, setShowGuide] = useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [csv, setCsv] = useState('');
    const [result, setResult] = useState<ImportResult | null>(null);
    const [validating, setValidating] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const [dragging, setDragging] = useState(false);

    const [filter, setFilter] = useState<Filter>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const fileInput = useRef<HTMLInputElement>(null);
    const busy = validating || importing;

    // El tipo queda en la URL (?type=) para que se pueda volver a la misma importación
    useEffect(() => {
        navigate({ search: `?type=${type}` }, { replace: true });
    }, [type]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let active = true;
        setLoadingTemplate(true);
        setTemplate(null);
        loadImportTemplate(type)
            .then(t => active && setTemplate(t))
            .catch(e => active && setError(e.message))
            .finally(() => active && setLoadingTemplate(false));
        return () => { active = false; };
    }, [type]);

    // El archivo se valida contra el tipo elegido: al cambiar el tipo se vuelve a validar
    useEffect(() => {
        if (csv) validate(csv);
    }, [type]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setPage(1); }, [filter, search, result]);

    const validate = async (content: string) => {
        setValidating(true);
        setError('');
        setResult(null);
        try {
            const validation = await importProducts(type, content, true);
            setResult(validation);
            setFilter(validation.summary.errors > 0 ? 'error' : 'all');
            setSearch('');
        } catch (e: any) {
            setError(e.message);
        } finally {
            setValidating(false);
        }
    };

    const handleFile = async (selected: File | null) => {
        setError('');
        setResult(null);
        setCsv('');
        setFile(selected);
        if (!selected) return;

        const invalid = validateCsvFile(selected);
        if (invalid) {
            setError(invalid);
            return;
        }
        const content = await readCsvFile(selected);
        setCsv(content);
        validate(content);
    };

    const clearFile = () => {
        setFile(null);
        setCsv('');
        setResult(null);
        setError('');
        if (fileInput.current) fileInput.current.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        if (busy) return;
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) handleFile(dropped);
    };

    const goBack = () => navigate('/products');

    const handleImport = async () => {
        if (!csv || !result) return;
        setImporting(true);
        setError('');
        try {
            const imported = await importProducts(type, csv, false);
            if (!imported.imported) {
                // Algo cambió entre la vista previa y la importación (otro usuario, un proveedor eliminado...)
                setResult(imported);
                setFilter('error');
                setError('No se importó ningún producto: el archivo tiene errores. Corríjalos y vuelva a cargarlo.');
                return;
            }
            const { create, update, unchanged } = imported.summary;
            const parts = [
                create > 0 ? `${create} producto(s) nuevo(s)` : '',
                update > 0 ? `${update} actualizado(s)` : '',
                unchanged > 0 ? `${unchanged} sin cambios` : '',
            ].filter(Boolean);
            navigate('/products', { state: { importMessage: `Importación completada: ${parts.join(', ')}.` } });
        } catch (e: any) {
            setError(`No se importó ningún producto. ${e.message}`);
        } finally {
            setImporting(false);
        }
    };

    // Reporte de las filas con errores para corregir el archivo
    const downloadErrors = () => {
        const rows = (result?.rows ?? []).filter(r => r.action === 'error');
        downloadCsv(`errores_importacion_${type === 'inventory' ? 'productos_con_inventario' : 'productos'}.csv`, rows.map(r => ({
            linea: r.line,
            codigo: r.code,
            nombre: r.name,
            errores: r.errors.join(' | '),
        })));
    };

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (result?.rows ?? [])
            .filter(r => filter === 'all' || r.action === filter)
            .filter(r => !term || [r.code, r.name, r.menu].some(v => (v ?? '').toLowerCase().includes(term)));
    }, [result, filter, search]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const summary = result?.summary;
    const hasFileErrors = (result?.fileErrors.length ?? 0) > 0;
    const toApply = summary ? summary.create + summary.update : 0;
    const canImport = !!summary && summary.errors === 0 && !hasFileErrors && toApply > 0 && !busy;

    // Paso actual del indicador de progreso
    const currentStep = summary && !hasFileErrors ? 3 : file ? 2 : 1;

    // =============================
    // Render
    // =============================
    const stepper = (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
            {['Tipo y plantilla', 'Archivo CSV', 'Revisión e importación'].map((label, i) => {
                const number = i + 1;
                const done = number < currentStep;
                const active = number === currentStep;
                return (
                    <React.Fragment key={label}>
                        {i > 0 && <div className="flex-grow-1 border-top d-none d-md-block" style={{ minWidth: 24 }} />}
                        <div className="d-flex align-items-center gap-2">
                            <span
                                className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold ${done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-light text-muted border'}`}
                                style={{ width: 28, height: 28, fontSize: 13 }}
                            >
                                {done ? <i className="bi bi-check-lg" /> : number}
                            </span>
                            <span className={`small ${active ? 'fw-semibold' : 'text-muted'}`}>{label}</span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    const typeOption = (option: ImportType) => (
        <label
            key={option}
            className={`border rounded p-3 d-flex gap-2 mb-2 ${type === option ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
            style={{ cursor: busy ? 'default' : 'pointer' }}
        >
            <input type="radio" className="form-check-input mt-1" name="importType" checked={type === option} disabled={busy}
                onChange={() => setType(option)} />
            <div>
                <div className="fw-semibold">
                    <i className={`bi ${option === 'inventory' ? 'bi-box-seam' : 'bi-bag'} me-1`} /> {IMPORT_LABELS[option].short}
                </div>
                <div className="text-muted small">{IMPORT_LABELS[option].description}</div>
            </div>
        </label>
    );

    const guide = template && (
        <div>
            <div className="table-responsive">
                <table className="table table-sm align-middle small mb-3">
                    <thead>
                        <tr className="text-muted text-uppercase">
                            <th>Columna</th>
                            <th>Requerida</th>
                            <th>Descripción</th>
                            <th>Ejemplo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {template.columns.map(column => (
                            <tr key={column.key}>
                                <td><code>{column.key}</code></td>
                                <td>{column.required ? <span className="badge bg-danger-subtle text-danger-emphasis">Sí</span> : <span className="text-muted">No</span>}</td>
                                <td>{column.description}</td>
                                <td className="text-muted text-nowrap">{column.example || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="fw-medium small mb-1">Unidades de medida (<code>unidad_medida</code>)</div>
                    <div className="border rounded" style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <table className="table table-sm small mb-0">
                            <tbody>
                                {template.units.map(u => (
                                    <tr key={u.id}><td><code>{u.id}</code></td><td>{u.description}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="fw-medium small mb-1">Tipos de impuesto (<code>tipo_impuesto</code>)</div>
                    <div className="border rounded" style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <table className="table table-sm small mb-0">
                            <tbody>
                                {template.taxCodes.map(t => (
                                    <tr key={t.id}><td><code>{t.id}</code></td><td>{t.description}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

    const summaryCards: { key: Filter; label: string; value: number; className: string }[] = summary ? [
        { key: 'all', label: 'Filas', value: summary.total, className: 'text-body' },
        { key: 'create', label: 'Nuevos', value: summary.create, className: 'text-success' },
        { key: 'update', label: 'Se actualizan', value: summary.update, className: 'text-primary' },
        { key: 'unchanged', label: 'Sin cambios', value: summary.unchanged, className: 'text-muted' },
        { key: 'error', label: 'Con errores', value: summary.errors, className: 'text-danger' },
    ] : [];

    const preview = summary && !hasFileErrors && (
        <>
            {/* Contadores: también filtran la tabla */}
            <div className="row g-2 mb-3">
                {summaryCards.map(card => (
                    <div className="col" key={card.key}>
                        <button
                            type="button"
                            className={`w-100 border rounded p-2 text-center bg-white ${filter === card.key ? 'border-primary border-2' : ''}`}
                            onClick={() => setFilter(card.key)}
                            title={`Ver: ${card.label.toLowerCase()}`}
                        >
                            <div className="text-muted small">{card.label}</div>
                            <div className={`fs-5 fw-semibold ${card.className}`}>{card.value.toLocaleString('en-US')}</div>
                        </button>
                    </div>
                ))}
            </div>

            {summary.errors > 0 ? (
                <div className="alert alert-danger py-2 small d-flex flex-wrap align-items-center gap-2">
                    <div className="flex-grow-1">
                        <i className="bi bi-exclamation-triangle me-1" />
                        Hay {summary.errors} fila(s) con errores. Corrija el archivo y vuelva a cargarlo: no se importa nada hasta que todas las filas sean válidas.
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={downloadErrors}>
                        <i className="bi bi-download me-1" /> Descargar errores
                    </button>
                </div>
            ) : toApply === 0 ? (
                <div className="alert alert-info py-2 small">
                    <i className="bi bi-info-circle me-1" /> Los productos del archivo ya están igual en el sistema: no hay cambios para importar.
                </div>
            ) : (
                <div className="alert alert-success py-2 small">
                    <i className="bi bi-check-circle me-1" /> El archivo es válido. Revise los cambios y presione <b>Importar</b>.
                </div>
            )}

            <div className="input-group mb-2" style={{ maxWidth: 420 }}>
                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                <input className="form-control" placeholder="Buscar por código, producto o menú..." value={search} onChange={(e) => setSearch(e.target.value)} />
                {search && (
                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setSearch('')}>
                        <i className="bi bi-x-lg" />
                    </button>
                )}
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle small mb-0">
                    <thead>
                        <tr className="text-muted text-uppercase">
                            <th style={{ width: 70 }}>Línea</th>
                            <th>Código</th>
                            <th>Producto</th>
                            <th>Menú</th>
                            <th>Resultado</th>
                            <th style={{ minWidth: 260 }}>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map(row => (
                            <tr key={row.line} className={row.action === 'error' ? 'table-danger' : ''}>
                                <td className="text-muted">{row.line}</td>
                                <td className="text-nowrap">{row.code || '—'}</td>
                                <td>{row.name || '—'}</td>
                                <td className="text-muted">{row.menu || '—'}</td>
                                <td><span className={`badge ${ACTION_BADGES[row.action].className}`}>{ACTION_BADGES[row.action].label}</span></td>
                                <td>
                                    {row.errors.map((e, i) => <div key={`e${i}`} className="text-danger"><i className="bi bi-x-circle me-1" />{e}</div>)}
                                    {row.notes.map((n, i) => <div key={`n${i}`} className="text-muted">{n}</div>)}
                                </td>
                            </tr>
                        ))}
                        {pageRows.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted py-4">No hay filas para mostrar.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                <span className="text-muted small">
                    {filteredRows.length > 0
                        ? `Mostrando ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, filteredRows.length)} de ${filteredRows.length} filas`
                        : ''}
                </span>
                {totalPages > 1 ? <Paginator totalPage={totalPages} pageActual={page} onChange={(p: number) => setPage(p)} /> : <div />}
            </div>
        </>
    );

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>

            {/* Encabezado */}
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <button type="button" className="btn btn-link text-decoration-none px-0 mb-1" onClick={goBack} disabled={importing}>
                        <i className="bi bi-arrow-left me-1" /> Productos y menús
                    </button>
                    <h3 className="mb-1">{IMPORT_LABELS[type].title}</h3>
                    <p className="text-muted mb-0">Cargue un archivo CSV con un producto por fila. Antes de guardar se valida todo el archivo.</p>
                </div>
            </div>

            {stepper}

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}

            <div className="row g-3">
                {/* Configuración: tipo, plantilla y archivo */}
                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body">
                            <h6 className="mb-3"><span className="badge rounded-pill bg-primary me-2">1</span>¿Qué desea importar?</h6>
                            {typeOption('products')}
                            {typeOption('inventory')}
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body">
                            <h6 className="mb-2"><span className="badge rounded-pill bg-primary me-2">2</span>Plantilla</h6>
                            <p className="text-muted small mb-2">Descárguela y complétela con sus productos. Trae una fila de ejemplo que debe reemplazar.</p>
                            <button type="button" className="btn btn-outline-primary w-100 mb-2" disabled={!template} onClick={() => template && downloadTemplate(template)}>
                                {loadingTemplate
                                    ? <><span className="spinner-border spinner-border-sm me-1" /> Cargando...</>
                                    : <><i className="bi bi-download me-1" /> Descargar plantilla</>}
                            </button>
                            <ul className="small text-muted mb-0 ps-3">
                                <li>Si el <b>código</b> ya existe, el producto se actualiza; si no, se crea.</li>
                                <li>Los productos que no estén en el archivo no se modifican.</li>
                                <li>Para editar sus productos actuales use <b>Exportar</b> en la pantalla de productos: el archivo tiene la misma estructura.</li>
                                <li>En Excel guarde con "CSV UTF-8 (delimitado por comas)". También se acepta punto y coma (;).</li>
                            </ul>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="mb-3"><span className="badge rounded-pill bg-primary me-2">3</span>Archivo CSV</h6>
                            <input ref={fileInput} type="file" accept=".csv,text/csv" className="d-none" disabled={busy}
                                onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />

                            {file ? (
                                <div className="border rounded p-3 d-flex align-items-center gap-2">
                                    <i className="bi bi-filetype-csv fs-3 text-success" />
                                    <div className="flex-grow-1 text-truncate">
                                        <div className="fw-medium text-truncate">{file.name}</div>
                                        <div className="text-muted small">{formatSize(file.size)}</div>
                                    </div>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" title="Volver a validar el archivo" disabled={!csv || busy} onClick={() => validate(csv)}>
                                        <i className="bi bi-arrow-clockwise" />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" title="Cambiar archivo" disabled={busy} onClick={clearFile}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    className={`border border-2 rounded p-4 text-center ${dragging ? 'border-primary bg-primary-subtle' : ''}`}
                                    style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                    onClick={() => fileInput.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={handleDrop}
                                >
                                    <i className="bi bi-cloud-arrow-up fs-2 text-primary" />
                                    <div className="fw-medium">Arrastre el archivo aquí</div>
                                    <div className="text-muted small">o haga clic para seleccionarlo</div>
                                    <div className="text-muted small mt-2">Solo .csv, máximo {MAX_FILE_MB} MB (5000 productos).</div>
                                </div>
                            )}

                            {validating && (
                                <div className="text-muted small mt-3"><span className="spinner-border spinner-border-sm me-2" /> Validando el archivo...</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Vista previa (o la guía mientras no hay archivo) */}
                <div className="col-12 col-lg-8">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            {result && hasFileErrors && (
                                <div className="alert alert-danger py-2 small">
                                    <div className="fw-semibold mb-1"><i className="bi bi-exclamation-triangle me-1" /> El archivo no se puede importar</div>
                                    <ul className="mb-0 ps-3">{result.fileErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                </div>
                            )}

                            {result && result.warnings.length > 0 && (
                                <div className="alert alert-warning py-2 small">
                                    {result.warnings.map((w, i) => <div key={i}><i className="bi bi-info-circle me-1" />{w}</div>)}
                                </div>
                            )}

                            {preview ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">Vista previa</h6>
                                        <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={() => setShowGuide(v => !v)}>
                                            <i className={`bi ${showGuide ? 'bi-chevron-up' : 'bi-info-circle'} me-1`} />
                                            {showGuide ? 'Ocultar guía' : 'Ver guía de columnas'}
                                        </button>
                                    </div>
                                    {showGuide && <div className="border rounded p-3 mb-3">{guide}</div>}
                                    {preview}
                                </>
                            ) : validating ? (
                                <div className="text-center text-muted py-5">
                                    <span className="spinner-border mb-2" />
                                    <div>Validando el archivo...</div>
                                </div>
                            ) : (
                                <>
                                    <h6 className="mb-1">Guía de columnas</h6>
                                    <p className="text-muted small">Así debe estar armado el archivo. Al cargarlo, aquí verá la vista previa de cada producto con sus errores.</p>
                                    {loadingTemplate
                                        ? <div className="text-center text-muted py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</div>
                                        : guide}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra de acciones */}
            <div className="d-flex justify-content-end align-items-center gap-2 border-top bg-body py-3 mt-3" style={{ position: 'sticky', bottom: 0, zIndex: 10 }}>
                {summary && !hasFileErrors && toApply > 0 && summary.errors === 0 && (
                    <span className="text-muted small me-auto">
                        Se crearán {summary.create} y se actualizarán {summary.update} producto(s).
                    </span>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={goBack} disabled={importing}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={handleImport} disabled={!canImport}>
                    {importing
                        ? <><span className="spinner-border spinner-border-sm me-1" /> Importando...</>
                        : <><i className="bi bi-upload me-1" /> {toApply > 0 ? `Importar ${toApply} producto(s)` : 'Importar'}</>}
                </button>
            </div>

            <div style={{ height: 40 }} />
        </div>
    );
};
